/*================================================================================================*/

/*

This routine checks the credit card number. The following checks are made:

1. A number has been provided
2. The number is a right length for the card
3. The number has an appropriate prefix for the card
4. The number has a valid modulus 10 number check digit if required

If the validation fails an error is reported.

The structure of credit card formats was gleaned from a variety of sources on the web, although the
best is probably on Wikepedia ("Credit card number"):

  http://en.wikipedia.org/wiki/Credit_card_number

Parameters:
            cardnumber           number on the card
            cardname             name of card as defined in the card list below

Author:     John Gardner
Date:       1st November 2003
Updated:    26th Feb. 2005      Additional cards added by request
Updated:    27th Nov. 2006      Additional cards added from Wikipedia
Updated:    18th Jan. 2008      Additional cards added from Wikipedia
Updated:    26th Nov. 2008      Maestro cards extended
Updated:    19th Jun. 2009      Laser cards extended from Wikipedia
Updated:    11th Sep. 2010      Typos removed from Diners and Solo definitions (thanks to Noe Leon)
Updated:    10th April 2012     New matches for Maestro, Diners Enroute and Switch
Updated:    17th October 2012   Diners Club prefix 38 not encoded
*/

/*
   If a credit card number is invalid, an error reason is loaded into the global ccErrorNo variable.
   This can be be used to index into the global error  string array to report the reason to the user
   if required:

   e.g. if (!checkCreditCard (number, name) alert (ccErrors(ccErrorNo);
*/

export function checkCreditCard(cardNumber: string, cardName: string): boolean {
    // Array to hold the permitted card characteristics
    const cards = [];

    // Define the cards we support. You may add addtional card types as follows.
    //  Name:         As in the selection box of the form - must be same as user's
    //  Length:       List of possible valid lengths of the card number for the card
    //  prefixes:     List of possible prefixes for the card
    //  checkdigit:   Boolean to say whether there is a check digit

    // we will only be using visa, discover, mastercard and amex
    cards[0] = {
        name: 'Visa',
        length: '13,16',
        prefixes: '4',
        checkdigit: true,
    };
    cards[1] = {
        name: 'MasterCard',
        length: '16',
        prefixes: '51,52,53,54,55,22,23,24,25,26,27',
        checkdigit: true,
    };
    cards[2] = {
        name: 'DinersClub',
        length: '14,16',
        prefixes: '36,38,54,55',
        checkdigit: true,
    };
    cards[3] = {
        name: 'CarteBlanche',
        length: '14',
        prefixes: '300,301,302,303,304,305',
        checkdigit: true,
    };
    cards[4] = {
        name: 'AmEx',
        length: '15',
        prefixes: '34,37',
        checkdigit: true,
    };
    cards[5] = {
        name: 'Discover',
        length: '16',
        prefixes: '6011,622,64,65',
        checkdigit: true,
    };
    cards[6] = {
        name: 'JCB',
        length: '16',
        prefixes: '35',
        checkdigit: true,
    };
    cards[7] = {
        name: 'enRoute',
        length: '15',
        prefixes: '2014,2149',
        checkdigit: true,
    };
    cards[8] = {
        name: 'Solo',
        length: '16,18,19',
        prefixes: '6334,6767',
        checkdigit: true,
    };
    cards[9] = {
        name: 'Switch',
        length: '16,18,19',
        prefixes: '4903,4905,4911,4936,564182,633110,6333,6759',
        checkdigit: true,
    };
    cards[10] = {
        name: 'Maestro',
        length: '12,13,14,15,16,18,19',
        prefixes: '5018,5020,5038,6304,6759,6761,6762,6763',
        checkdigit: true,
    };
    cards[11] = {
        name: 'VisaElectron',
        length: '16',
        prefixes: '4026,417500,4508,4844,4913,4917',
        checkdigit: true,
    };
    cards[12] = {
        name: 'LaserCard',
        length: '16,17,18,19',
        prefixes: '6304,6706,6771,6709',
        checkdigit: true,
    };

    // Establish card type
    let cardType = cards.filter((card) => cardName === card.name);
    // If card type not found, report an error
    if (cardType.length !== 1) {
        return false;
    }
    // @ts-ignore
    cardType = cardType[0];

    // Ensure that the user has provided a credit card number
    if (cardNumber.length === 0) {
        return false;
    }

    // Check that the number is numeric and of a valid length
    const cardNo = cardNumber.replace(/\s/g, '');
    const cardexp = /^[0-9]{13,19}$/;
    if (!cardexp.exec(cardNo)) {
        return false;
    }

    // Now check the modulus 10 check digit - if required
    // @ts-ignore
    if (cardType.checkdigit) {
        let checksum = 0; // running checksum total
        let j = 1; // takes value of 1 or 2

        // Process each digit one by one starting at the right
        let calc;
        for (let i = cardNo.length - 1; i >= 0; i -= 1) {
            // Extract the next digit and multiply by 1 or 2 on alternative digits.
            calc = Number(cardNo.charAt(i)) * j;

            // If the result is in two digits add 1 to the checksum total
            if (calc > 9) {
                checksum += 1;
                calc -= 10;
            }

            // Add the units element to the checksum total
            checksum += calc;

            // Switch the value of j
            if (j === 1) {
                j = 2;
            } else {
                j = 1;
            }
        }

        // All done - if checksum is divisible by 10, it is a valid modulus 10.
        // If not, report an error.
        if (checksum % 10 !== 0) {
            return false;
        }
    }

    // Check it's not a spam number
    if (cardNo === '5490997771092064') {
        return false;
    }

    // check the cards prefix
    let prefixValid = false;
    // @ts-ignore
    const prefixes = cardType.prefixes.split(',');
    for (const prefix of prefixes) {
        const exp = new RegExp('^' + prefix);
        if (exp.test(cardNo)) prefixValid = true;
    }
    if (!prefixValid) {
        return false;
    }

    // See if the length is valid for this card
    let lengthValid = false;
    // @ts-ignore
    const lengths = cardType.length.split(',');
    for (const len of lengths) {
        if (cardNo.length === +len) lengthValid = true; // +len converts len to int value
    }
    if (!lengthValid) {
        return false;
    }

    // The credit card is in the required format.
    return true;
}

/*================================================================================================*/

// american express check removed as we no longer support
// add or remove other credit cards here as required
export function getCardType(value: string): {
    type: 'MasterCard' | 'Visa' | 'DinersClub' | 'Maestro' | 'Discover' | 'AmEx' | '';
    maxLength: number;
} {
    //first check for MasterCard
    if (/^ *(5[1-5]|2[2-7])/.test(value)) {
        return {
            type: 'MasterCard',
            maxLength: 16,
        };
    }
    //then check for Discover
    if (/^ *6(011|[4-5]|22)/.test(value)) {
        return {
            type: 'Discover',
            maxLength: 16,
        };
    }

    //then check for AmEx
    if (/^ *3(4|7)/.test(value)) {
        return {
            type: 'AmEx',
            maxLength: 15,
        };
    }

    // then check for Visa
    if (/^ *4/.test(value)) {
        return {
            type: 'Visa',
            maxLength: 16,
        };
    }
    //then check for Diners club
    if (/^ *(36|38)/.test(value)) {
        return {
            type: 'DinersClub',
            maxLength: 16,
        };
    }
    //then check for Maestro
    if (/^ *(50(018|20|58))|6304|(67(59|61|62|63))/.test(value)) {
        return {
            type: 'Maestro',
            maxLength: 19,
        };
    }
    // when card not detected
    return {
        type: '',
        maxLength: 16,
    };
}
